import React, { useMemo } from "react";
import { Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { dismissAdminMessage, seenAdminMessage } from "../api/adminMessagesApi";
import { useRemindersHub } from "../context/RemindersHubContext";
import { useAgentBillingStatus } from "../../../contexts/AgentBillingStatusContext";
import "./AdminMessageBanner.scss";

/**
 * באנר מערכת יחיד בדשבורד הסוכן.
 *
 * סדר עדיפויות (מוצג תמיד רק הבאנר העליון):
 *   1. billing_migration (legacy_unbilled) — חוסם את השימוש המלא, לא ניתן לסגירה.
 *   2. admin broadcast — urgent.
 *   3. admin broadcast — important.
 *   4. admin broadcast — normal.
 *
 * כל המקורות מאוחדים כאן כדי למנוע כפילות UI.
 */

const ADMIN_PRIORITY_RANK = { urgent: 2, important: 3, normal: 4 };
const BILLING_MIGRATION_RANK = 1;

function normalizeAdminBanner(banner) {
  if (!banner) return null;
  const pr = (banner.priority || "normal").toLowerCase();
  return {
    source: "admin",
    id: banner.id,
    rank: ADMIN_PRIORITY_RANK[pr] ?? ADMIN_PRIORITY_RANK.normal,
    priority: pr,
    title: banner.title,
    body: banner.body,
    cta_link: banner.cta_link,
    cta_text: banner.cta_text,
    dismissible: true,
  };
}

function buildBillingMigrationBanner(active) {
  if (!active) return null;
  return {
    source: "system",
    id: "system:billing_migration",
    rank: BILLING_MIGRATION_RANK,
    priority: "urgent",
    title: "נדרשת הסדרת אמצעי תשלום",
    body: "כדי להמשיך להשתמש במערכת באופן מלא, יש להשלים הסדרת אמצעי תשלום.",
    cta_link: null,
    cta_text: null,
    // באנר מערכתי שמבטא gate — לא ניתן לסגירה ע״י הסוכן.
    dismissible: false,
  };
}

export default function AdminMessageBanner() {
  const { activeAdmin, refreshActiveAdmin, refreshSummary } = useRemindersHub();
  const { showBillingMigrationGate } = useAgentBillingStatus();
  const topBanner = useMemo(() => {
    const candidates = [
      buildBillingMigrationBanner(showBillingMigrationGate),
      ...(activeAdmin.banners || []).map(normalizeAdminBanner),
    ].filter(Boolean);
    candidates.sort((a, b) => a.rank - b.rank);
    return candidates[0] || null;
  }, [showBillingMigrationGate, activeAdmin.banners]);

  if (!topBanner) return null;

  const isUrgent = topBanner.priority === "urgent";
  const isImportant = topBanner.priority === "important";
  const isSystem = topBanner.source === "system";

  const onSeen = async () => {
    if (isSystem) return;
    try {
      await seenAdminMessage(topBanner.id);
    } catch {
      /* ignore */
    }
  };

  const onDismiss = async () => {
    if (!topBanner.dismissible || isSystem) return;
    try {
      await dismissAdminMessage(topBanner.id);
      await refreshActiveAdmin();
      await refreshSummary();
    } catch {
      /* ignore */
    }
  };

  // באנר system של billing_migration מקבל עיצוב "important" (כתום) —
  // מובחן מ-"urgent" (אדום) של admin broadcast, ותואם לצבע Tag הקיים
  // של legacy_unbilled בשאר המערכת.
  const visualClass = isSystem
    ? "hr-admin-banner--important hr-admin-banner--system"
    : (isUrgent
        ? "hr-admin-banner--urgent"
        : isImportant
          ? "hr-admin-banner--important"
          : "");

  return (
    <div
      className={`hr-admin-banner ${visualClass}`.trim()}
      role="region"
      aria-label={isSystem ? "התראת מערכת" : "הודעת מערכת"}
      aria-live={isSystem || isUrgent ? "assertive" : "polite"}
      data-banner-source={topBanner.source}
    >
      <div className="hr-admin-banner__inner">
        <strong className="hr-admin-banner__title">{topBanner.title}</strong>
        {topBanner.body ? (
          <span className="hr-admin-banner__body">{topBanner.body}</span>
        ) : null}
        {topBanner.cta_link && topBanner.cta_text ? (
          <Button
            size="small"
            variant="contained"
            color="inherit"
            className="hr-admin-banner__cta"
            component="a"
            href={topBanner.cta_link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onSeen}
          >
            {topBanner.cta_text}
          </Button>
        ) : null}
      </div>
      {topBanner.dismissible ? (
        <IconButton
          size="small"
          aria-label="סגור"
          onClick={onDismiss}
          className="hr-admin-banner__close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      ) : null}
    </div>
  );
}
